import {
  createRootRoute, createRoute, createRouter, lazyRouteComponent, redirect,
} from "@tanstack/react-router";
import { Schale } from "./schale/Schale";
import { Heute } from "./masken/Heute";
import { rechtZiel, rollenJetzt, wegZiel } from "./schale/rechte";
import { umleitungMerken } from "./schale/umleitung";
import { istTelefon, useTelefon } from "./schale/geraet";
import { MehrSeite } from "./schale/Mehrblatt";
import { PreisTelefon } from "./masken/PreisTelefon";
import { nachschlagenOeffnen } from "./schale/nachschlagen";
import { sucheOeffnen } from "./schale/suchschalter";
import { nachschlagenVorbelegen } from "./masken/NachschlagenTelefon";
import { belegadresse } from "./daten/belege";
import { neueAdresse } from "./schale/altadressen";
import { seiteAusAdresse } from "./masken/einstellungen/seiten";
import { Ladefehler } from "./schale/Ladefehler";
import { Unbekannt } from "./schale/Unbekannt";
import { fassungBeimWegwechsel } from "./pwa/anmelden";

/* Die Startseite kommt fest mit — sie ist die erste Maske nach der
   Anmeldung, und ein Ladebalken davor wäre ein Umweg um nichts.

   Alle anderen werden erst geholt, wenn jemand hingeht. Das Brett allein
   ist die größte Maske der Anwendung; sie im Anmeldebild mitzuladen
   heißt, jeden Morgen für etwas zu warten, das man vielleicht gar nicht
   öffnet. `defaultPreload: "intent"` holt sie beim Überfahren des
   Menüpunkts — bis zum Klick ist sie dann meistens da. */
const spaet = lazyRouteComponent;
const Vermieten = spaet(() => import("./masken/Vermieten"), "Vermieten");
const Brett = spaet(() => import("./masken/planung/Planung"), "Planung");

const Agenda = spaet(() => import("./masken/planung/Agenda"), "Agenda");
/* Einteilen (docs/65, 08.10.2026): wer macht was, als Woche — nur am Schreibtisch. */
const Einteilen = spaet(() => import("./masken/disposition/Einteilen"), "Einteilen");

/* ══════════════ Die Weiche vor dem Brett ══════════════

   **Am Telefon zeichnet `/kalender` eine Agenda** (seit 17.09.2026,
   `masken/planung/Agenda.tsx`), sonst das Brett. Luca: *„Ich will alles
   vollständig mobile-optimiert haben."* Bis dahin stand hier ein Satz,
   wem der Schirm gehört (`BrettAmTelefon.tsx`, gelöscht) — ein
   Belegungsband auf 390 px ist Raten statt Sehen, und das bleibt richtig:
   Deshalb ist es am Telefon kein kleineres Brett, sondern eine Liste je
   Ressource mit Karten, „frei ab" und Umplanen über das Blatt.

   Sie steht **hier** und nicht in der Maske: Das Brett soll nichts davon
   wissen müssen, auf welchem Gerät es gerade nicht gezeichnet wird — und
   am Telefon wird es so gar nicht erst geholt, die Agenda am Schreibtisch
   ebenso wenig. Das Tablet bekommt das Brett, für den Finger gebaut
   (`stile/brett.css`, „Das Brett mit dem Finger").

   `preload` wird weitergereicht, sonst verlöre das Brett genau das, was
   der Kommentar oben ihm zuschreibt: Es käme erst beim Klick und nicht
   schon beim Überfahren des Menüpunkts. Weitergereicht wird es als
   eigene Zeile und nicht als Wert — `lazyRouteComponent` setzt `preload`
   nach dem ersten Laden auf `undefined`, und eine Kopie von damals
   riefe danach ins Leere. */
function Planung() {
  return useTelefon() ? <Agenda /> : <Brett />;
}
Planung.preload = () => (istTelefon() ? Agenda.preload?.() : Brett.preload?.());
const Schnellerfassung = spaet(() => import("./masken/Schnellerfassung"), "Schnellerfassung");
const Angebote = spaet(() => import("./masken/vorgaenge/Angebote"), "Angebote");
/* `/angebote/neu` und `/angebote/$nr` zeichnet **ein** Rahmen
   (masken/vorgaenge/AngebotNeu.tsx): am Telefon unter `neu` der Ablauf in
   vier Griffen, sonst der Entwurf. Warum ein Rahmen und nicht zwei
   Komponenten, steht unten bei `angebotRahmen`. */
const AngebotRahmen = spaet(() => import("./masken/vorgaenge/AngebotNeu"), "AngebotRahmen");
const Auftraege = spaet(() => import("./masken/vorgaenge/Auftraege"), "Auftraege");
const Aufnahmen = spaet(() => import("./masken/vorgaenge/Aufnahmen"), "Aufnahmen");
const Vorgang = spaet(() => import("./masken/vorgaenge/Vorgang"), "Vorgang");
const Kunden = spaet(() => import("./masken/kunden/Kunden"), "Kunden");
const SucheSeite = spaet(() => import("./masken/SucheTelefon"), "SucheSeite");
const Kunde = spaet(() => import("./masken/kunden/Kunde"), "Kunde");
const KundeNeu = spaet(() => import("./masken/kunden/KundeNeu"), "KundeNeu");
const Ressourcen = spaet(() => import("./masken/stamm/Ressourcen"), "Ressourcen");
const Preise = spaet(() => import("./masken/stamm/Preise"), "Preise");
const Weiteres = spaet(() => import("./masken/stamm/Weiteres"), "Weiteres");
const Abrechnung = spaet(() => import("./masken/finanzen/Abrechnung"), "Abrechnung");
const Belege = spaet(() => import("./masken/finanzen/Belege"), "Belege");
const Einstellungen = spaet(() => import("./masken/einstellungen/Einstellungen"), "Einstellungen");
/* **Das Handbuch** (21.09.2026) — `docs/47-handbuch.md`, dargestellt. Luca:
   *„Das Handbuch sollte eingebettet sein."* Es kommt spät wie jede andere
   Maske; der Text selbst und `marked` kommen noch einmal später, als eigener
   Brocken aus der Maske heraus (`masken/handbuch/Handbuch.tsx`). */
const Handbuch = spaet(() => import("./masken/handbuch/Handbuch"), "Handbuch");
/* **Mein Konto** (E4, 04.10.2026): der zweite Faktor — einrichten, entfernen. */
const Konto = spaet(() => import("./masken/Konto"), "Konto");
/* **Die Tagesmappe hat seit dem 21.09.2026 keinen eigenen Weg mehr.** Sie
   ist ein Abschnitt in `/heute` geworden (`masken/Handy.tsx` trägt nur noch
   ihre Bausteine); Luca: *„Wie kann man heute und Start zusammenbringen? …
   Das ist ja irgendwie Quatsch."* */
/* Der Einsatzschirm (17.09.2026) und seine Schritte — Übergabe und Rücknahme
   sind Schritte im Einsatz, keine eigenen Einstiege mehr. */
const HandyEinsatz = spaet(() => import("./masken/handy/Einsatz"), "Einsatz");
const HandyUebergabe = spaet(() => import("./masken/HandyProtokoll"), "HandyUebergabe");
const HandyRuecknahme = spaet(() => import("./masken/HandyProtokoll"), "HandyRuecknahme");
const HandyTagesabschluss = spaet(() => import("./masken/handy/Tagesabschluss"), "Tagesabschluss");
const HandyZusammenfassung = spaet(() => import("./masken/handy/Zusammenfassung"), "Zusammenfassung");
const HandyVorbereitung = spaet(() => import("./masken/HandyVorbereitung"), "HandyVorbereitung");
/* Die Schirme der unteren Leiste (17.09.2026). „Mehr" ist seit demselben
   Abend ein Blatt (`schale/Mehrblatt.tsx`), „Start" die Startseite, und
   „Zeiten" gibt es seit dem 18.09.2026 nicht mehr (siehe unten). Welcher
   Schirm zu welcher Rolle gehört, steht in `schale/handywege.ts`. */
const Ortsbegehung = spaet(() => import("./masken/handy/Ortsbegehung"), "Ortsbegehung");
const Nachfassen = spaet(() => import("./masken/handy/Nachfassen"), "Nachfassen");

/* ══════════════════════════════════════════════════════════════════
   Der Router.

   Die Wege heißen so, wie die Navigation sie nennt — `/auftraege`, nicht
   `/orders`. Sichtbare Begriffe und Adressen auseinanderlaufen zu lassen
   ist eine Fehlerquelle, die nichts einbringt: Wer im Betrieb von einem
   Auftrag spricht, soll ihn auch in der Adresszeile finden.

   Jede Route steht ausgeschrieben, keine über eine Hilfsfunktion: Der
   Router liest die Wege aus den Zeichenketten und prüft damit jedes
   `to="…"` in der Anwendung. Eine Fabrik mit `path: string` nimmt ihm
   genau das weg — dann heißt jeder Tippfehler im Weg erst zur Laufzeit
   „Seite nicht gefunden".

   Angebot und Auftrag sind zwei Phasen derselben Sache — und trotzdem
   zwei Wege. Wer ein Angebot sucht, geht auf Angebote; wer einen
   laufenden Auftrag sucht, auf Aufträge (28-designkonzept.md).
   ══════════════════════════════════════════════════════════════════ */

/* **Das Gerät entscheidet, nicht der Weg** (17.09.2026). Bis dahin stand
   hier die Zeile `matchMedia("(max-width: 640px)")` und sonst nichts —
   und ein Telefon im Querformat ist 844 px breit. Wer fragt, was das
   für ein Gerät ist, fragt jetzt `schale/geraet.ts`: Breite, Höhe und
   Zeigegerät, an **einer** Stelle für die ganze Anwendung. Luca: *„Die PWA
   muss ja automatisch erkennen, dass sie im Mobile-Modus ist. Das muss ja
   keine extra URL sein."* Seit dem 19.09.2026 heißt auch kein Weg mehr nach
   einem Gerät (`/handy` → `/heute`, `schale/altadressen.ts`). */
const amTelefon = istTelefon;

/* ══════════════ Der Wegwächter ══════════════

   Er steht an der Wurzel und damit **vor jeder Maske** — nicht in einer
   Komponente, die erst läuft, wenn die Maske schon geladen ist. Zwei
   Fragen beantwortet er, und `schale/rechte.ts` weiß beide:

   1. **Darf diese Rolle hierher?** Wenn nicht, geht es auf ihren
      Einstieg. Der technische Außendienst, der `/planung` tippt, landet
      auf seiner Tagesmappe und nicht auf einem Brett, das ihm die API
      ohnehin verweigert.
   2. **Ist das ein Telefon?** Dann gilt die mobile Fassung des Wegs.
      Luca, 16.09.2026: *„Das muss ja theoretisch automatisch erkannt
      werden, wenn man auf dem Handy unterwegs ist."*

   **Die Rolle steht nicht im React-Kontext**, sondern daneben
   (`rollenJetzt()`): Hier gibt es keinen Anbieter darüber. Solange sie
   `null` ist — der Wiedereinstieg aus dem Cookie läuft noch —, leitet
   er nichts um; `main.tsx` lässt ihn danach noch einmal prüfen.

   **Die zweite Frage stellt er nur beim Aufruf** (17.09.2026,
   40-mobiles-grundgeruest.md, Schritt 3). Bis dahin prüfte er nach jedem
   Lagewechsel beide, und wer ein Fenster von 1280 auf 600 px zog, verlor
   `/angebote` an `/handy/angebot` — beim Zurückziehen blieb er dort. Ein
   Lagewechsel ist kein Aufruf: Die Adresse bleibt, und die Maske zeichnet
   sich neu. Aufgerufen ist ein Weg, wenn sein Pfad ein anderer ist als
   beim letzten Prüfen — oder wenn Rolle oder große Ansicht gewechselt
   haben (`wegeNeuPruefen`, `main.tsx`). Gleicher Pfad mit anderen
   Suchwerten ist kein Aufruf: Ein Filter schickt niemanden weg.

   Umgeleitet wird mit `replace` — die Adresse, von der weggeschickt wird,
   bleibt nicht als Schritt im Verlauf stehen, sonst führte die Zurückgeste
   auf sie und von dort sofort wieder hierher. */
let geprueft: string | null = null;

/* **Wer nach der Lage weggeschickt wurde und zurückgeschickt wird, bleibt.**
   Gefunden am 17.09.2026 auf 390 × 844: `/planung` leitete nach
   `/handy/nachschlagen` um, und die Adresse öffnet seitdem das Blatt und
   führt dorthin zurück, wo man war — auf `/planung`. Der Wächter hielt das
   für einen neuen Aufruf und schickte wieder weg: „Too many redirects". Kommt
   der Weg, von dem eben nach der Lage umgeleitet wurde, gleich wieder an,
   fragt der Wächter nur noch das Recht. Am Telefon steht dann der Hinweis der
   Planung mit dem Nachschlagen darüber. */
let lageUmgeleitet: string | null = null;

/** Beim nächsten Laden wieder beide Fragen stellen — nach einem Rollen- oder Ansichtswechsel. */
export function wegeNeuPruefen(): void {
  geprueft = null;
  lageUmgeleitet = null;
}

/* Wege, die ein Blatt öffnen und dorthin zurückführen, wo man war — sie sind
   kein Ort. Eine neue Fassung wird bei ihnen nicht mitgenommen (unten): Das
   volle Laden landete auf dem Zwischenschritt, nicht auf der Maske. */
/* **Seit Signal (Paket 5) sind „Mehr" und „Preis" am Telefon Seiten** — dort
   sind sie ein Ort und kein Zwischenschritt. Am Schreibtisch öffnet `/preis`
   weiter das Nachschlagen, und `/mehr` führt auf den Einstieg. */
const BLATTWEG_IMMER = /^\/nachschlagen(\/|\?|$)/;
const BLATTWEG_SCHREIBTISCH = /^\/(preis|mehr)(\/|\?|$)/;
const IST_BLATTWEG = { test: (pfad: string): boolean =>
  BLATTWEG_IMMER.test(pfad) || (!istTelefon() && BLATTWEG_SCHREIBTISCH.test(pfad)) };

const wurzel = createRootRoute({
  component: Schale,
  beforeLoad: async ({ location, preload }) => {
    /* ── 0. Alte Adressen zuerst (19.09.2026) ──
       Eine Adresse unter `/handy…` wird übersetzt, **bevor** nach Recht oder
       Gerät gefragt wird (`schale/altadressen.ts`). Sonst sähe der Wächter
       einen Weg, den es nicht mehr gibt.

       **Seit dem 21.09.2026 führen sie alle auf `/heute`** — die Tabelle mit
       fünfzehn Übersetzungen ist weg. Gebraucht wird nur noch die Kachel,
       die zwischen dem 16. und 17.09. mit `start_url: "/handy"` auf einem
       iPhone gelandet ist; Tiefenlinks aus vier Tagen Testfassung teilt
       niemand. */
    const alt = neueAdresse(location.pathname);
    if (alt !== null) throw redirect({ href: alt, replace: true });

    const rollen = rollenJetzt();
    const pfad = location.pathname;
    const zurueckgeschickt = pfad === lageUmgeleitet;
    const aufruf = pfad !== geprueft && !zurueckgeschickt;
    const recht = rechtZiel(rollen, pfad, amTelefon());
    const ziel = recht ?? (aufruf ? wegZiel(rollen, pfad, amTelefon()) : null);
    // Gemerkt wird erst, wenn die Rolle feststeht: Vorher hat der Wächter
    // nichts geprüft, und der erste Blick mit Rolle ist der Aufruf. Und
    // nie beim Vorladen (`defaultPreload: "intent"`): Wer über einen
    // Verweis fährt, hat ihn nicht aufgerufen — sonst gälte der Klick
    // danach als „derselbe Weg" und die Lage würde nicht mehr gefragt.
    if (rollen !== null && !preload) {
      geprueft = pfad;
      if (zurueckgeschickt) lageUmgeleitet = null;
      else if (ziel !== null && recht === null) lageUmgeleitet = pfad;
    }
    if (ziel) {
      /* D-03: Nicht stumm umleiten — die Schale sagt beim nächsten Bild,
         warum man hier steht (`schale/umleitung.ts`). */
      if (!preload && ziel !== pfad) umleitungMerken(pfad, recht !== null ? "recht" : "telefon");
      throw redirect({ to: ziel, replace: true });
    }

    /* ── Eine neue Fassung reist mit dem Wegwechsel (19.09.2026) ──
       Luca: *„Ich möchte, dass es automatisch aktualisiert wird."* Der
       Wechsel auf eine andere Maske ist der Moment, in dem Neuladen nichts
       kostet: Die Maske, von der man kommt, wird ohnehin abgebaut. Steht eine
       neue Fassung bereit, wird aus dem Wechsel im Router deshalb ein volles
       Laden des Ziels (`pwa/anmelden.ts`) — dieselbe Adresse, derselbe
       Rückweg, nur schon die neue Fassung. Ohne neue Fassung ist das ein
       sofort erfülltes Versprechen und kostet nichts.

       Nicht beim Vorladen, nicht beim ersten Aufruf (das ist schon ein
       volles Laden) und nicht auf den Blattwegen oben — auch nicht auf
       `/suche`, die am Schreibtisch ein Fenster über der Maske ist. */
    if (!preload && letzterPfad !== null && pfad !== letzterPfad
      && !IST_BLATTWEG.test(pfad) && pfad !== "/suche") {
      await fassungBeimWegwechsel(location.href);
    }
  },
});

/* **`/start` ist seit dem 21.09.2026 eine Altadresse** und leitet auf
   `/heute`. Luca: *„Wie kann man heute und Start zusammenbringen? … Das ist
   ja irgendwie Quatsch."* Der Schirm heißt jetzt einmal, und wer den alten
   Weg im Lesezeichen hat, landet darauf (`schale/altadressen.ts` übersetzt
   ihn, bevor der Wächter fragt). */
const start = createRoute({
  getParentRoute: () => wurzel,
  path: "/start",
  beforeLoad: () => { throw redirect({ to: "/heute", replace: true }); },
  component: Heute,
});
/* ══════════ Verteilen heißt jetzt Einteilen — 08.10.2026 ══════════

   Verteilt wurde vom 25.09. an im Planungsbrett, im Abschnitt Personal.
   Seit dem 08.10.2026 hat das Einteilen eine eigene Seite unter
   „Disposition" (`/einteilen`, docs/65). **Die alte Adresse bleibt als
   Weiterleitung** — die Glocke und jede schon zugestellte Meldung zeigen auf
   `/verteilen` oder `/verteilen/JJJJ-MM-TT`. Sie führt nach `/einteilen`,
   mit dem Tag als `?tag=`; am Telefon auf die Startseite, wo der Abschnitt
   „Zu verteilen" steht.

   Die Seite selbst steht unten (`einteilen`, `masken/disposition/Einteilen.tsx`). */
const TAG_FORM = /^\d{4}-\d{2}-\d{2}$/;
function zumEinteilen(tag: string | undefined): never {
  if (istTelefon()) throw redirect({ to: "/heute", replace: true });
  throw redirect({ to: "/einteilen", search: tag && TAG_FORM.test(tag) ? { tag } : {}, replace: true });
}
const verteilen = createRoute({
  getParentRoute: () => wurzel,
  path: "/verteilen",
  beforeLoad: () => zumEinteilen(undefined),
});
const verteilenTag = createRoute({
  getParentRoute: () => wurzel,
  path: "/verteilen/$tag",
  beforeLoad: ({ params }) => zumEinteilen(params.tag),
});
/* ══════════ Einteilen — 08.10.2026 (docs/65, Variante A) ══════════

   Links „Offen", rechts die Woche der Leute. `?tag=` ist der erste der
   sieben Tage — wie früher beim Verteilen beginnt das Bild am Tag der
   Meldung; ohne `tag` am Montag dieser Woche. Das Blättern schreibt den
   ersten Tag zurück in die Adresse.

   **Am Telefon gibt es die Seite nicht** — dort steht „Zu verteilen" auf der
   Startseite (`masken/handy/Zuverteilen.tsx`), wie vorher `/verteilen`. */
const einteilen = createRoute({
  getParentRoute: () => wurzel,
  path: "/einteilen",
  component: Einteilen,
  /* `?aufnahme=<id>` markiert eine Ortsaufnahme (Paket aufnahmen2): „In
     Einteilen planen" aus Liste und Akte der Ortsaufnahme springt in die
     Woche ihres Termins bzw. Wunschs und hat sie gleich in der Hand. */
  validateSearch: (such: Record<string, unknown>): { tag?: string; aufnahme?: string } => ({
    ...(typeof such["tag"] === "string" && TAG_FORM.test(such["tag"]) ? { tag: such["tag"] } : {}),
    ...(typeof such["aufnahme"] === "string" && /^[0-9a-f-]{36}$/i.test(such["aufnahme"]) ? { aufnahme: such["aufnahme"] } : {}),
  }),
  /* Wie früher `/verteilen`: ohne Hinweis — „Zu verteilen" ist der Ort dafür am Telefon. */
  beforeLoad: () => { if (istTelefon()) throw redirect({ to: "/heute", replace: true }); },
});

/* ══════════ Aus der Planung wird der Kalender — 08.10.2026 ══════════

   Luca (docs/65): Die Seite „Planung" beantwortete zwei Fragen auf einer
   Fläche — was wann verplant ist und wer was macht. Daraus sind zwei Punkte
   unter „Disposition" geworden: **Kalender** (`/kalender`, das Brett) und
   **Einteilen** (`/einteilen`).

   **`/planung` bleibt als Weiterleitung** und nimmt alles mit, was in der
   Adresse steht (`vorgang`, `von`, `raster`) — Lesezeichen, Meldungen mit
   dem Weg `/planung` und Verweise in schon verschickten Texten landen so im
   Kalender, mit derselben Bedeutung. `?aufgaben` meinte „Verteilen im
   Brett" und führt jetzt nach Einteilen, auf den Tag aus `von`. */
type Kalenderstand = { vorgang?: string; von?: string; raster?: "tag" | "stunde" };
function kalenderstand(such: Record<string, unknown>): Kalenderstand {
  return {
    ...(typeof such["vorgang"] === "string" && such["vorgang"]
      ? { vorgang: such["vorgang"] }
      : {}),
    // Ein Tag, nicht irgendein Text: Was nicht JJJJ-MM-TT ist, kommt nicht
    // in die Rechnung, die den Wochenversatz bestimmt.
    ...(typeof such["von"] === "string" && TAG_FORM.test(such["von"])
      ? { von: such["von"] }
      : {}),
    // Die Auflösung. Alles andere ist kein halbes Raster, sondern keins —
    // dann gilt die Vorgabe, und das ist der Tag.
    ...(such["raster"] === "stunde" ? { raster: "stunde" as const } : {}),
  };
}
const planung = createRoute({
  getParentRoute: () => wurzel,
  path: "/planung",
  validateSearch: (such: Record<string, unknown>): Kalenderstand & { aufgaben?: true } => ({
    ...kalenderstand(such),
    ...(such["aufgaben"] === true || such["aufgaben"] === "true" || such["aufgaben"] === "1" || such["aufgaben"] === 1
      ? { aufgaben: true as const } : {}),
  }),
  beforeLoad: ({ search }) => {
    const { aufgaben, ...stand } = search;
    if (aufgaben) zumEinteilen(stand.von);
    throw redirect({ to: "/kalender", search: stand, replace: true });
  },
});
const kalender = createRoute({
  getParentRoute: () => wurzel,
  path: "/kalender",
  component: Planung,
  /* ══════════ Am Telefon gibt es die Planung nicht — 18.09.2026 ══════════

     Luca, beim Durchklicken der Handy-Ansicht: *„Das, was wirklich
     bescheuert aussieht, ist die Planung. Die stell hinten an. Das soll auf
     Mobile nicht zur Verfügung stehen. In Step 1 machen wir das später an."*

     **Die Adresse bleibt trotzdem stehen.** Lesezeichen und installierte
     Telefone kennen sie, und eine Adresse, die ins Leere läuft, ist
     schlimmer als eine, die woanders ankommt. Sie führt auf `/start` — das
     ist der Schirm, den jede Rolle am Telefon hat, und der, von dem aus die
     Zeilen der Planung ohnehin abgingen. `replace`, weil ein Ort, den es
     hier nicht gibt, kein Ort ist, zu dem man zurückwill (dieselbe Regel
     wie bei den alten Adressen unter `/handy`, `schale/altadressen.ts`,
     40-mobiles-grundgeruest.md, Schritt 4).

     **Die Agenda ist nicht gelöscht, nur ruhend** (`masken/planung/Agenda.tsx`):
     *„In Step 1 machen wir das später an."* Die Weiche oben in `Planung()`
     bleibt deshalb stehen — sie greift, wenn jemand am Schreibtisch auf
     `/kalender` steht und das Fenster schmal zieht: Ein Lagewechsel ist kein
     Aufruf, `beforeLoad` läuft dabei nicht, und dann ist die Agenda besser
     als ein Brett auf 390 px.

     **Am Schreibtisch und auf dem Tablet ändert sich nichts.** `istTelefon()`
     ist die eine Frage danach (`schale/geraet.ts`); das Brett bleibt dort
     die Hauptarbeit der Disposition. */
  beforeLoad: ({ preload }) => {
    if (istTelefon()) {
      // D-03: Wer hier weggeschickt wird, bekommt einen Satz dazu.
      if (!preload) umleitungMerken("/kalender", "telefon");
      throw redirect({ to: "/heute", replace: true });
    }
  },
  /* **Der Sprung aus einem Vorgang trägt den Vorgang mit.** Bis zum
     15.09.2026 führte „In der Planung anzeigen" auf ein leeres `/planung`:
     dieselbe Woche wie immer, keine Markierung — wer aus einem Auftrag im
     Oktober kam, suchte ihn von Hand. Luca: *„muss das in der Planung
     markiert werden, sodass man direkt sieht."* `vorgang` hebt hervor,
     `von` blättert in die richtige Woche. Beide sind freiwillig; `/kalender`
     ohne alles bleibt, was es war. */
  /* **`von` und `raster` sind der Stand des Bretts, nicht nur ein Sprungziel.**
     Seit dem 16.09.2026 steht beides in der Adresse und nicht mehr im
     Gedächtnis der Maske. Der Grund ist der Link: „Schau dir den 17. in
     Stunden an" ist ein Satz, den jemand schreibt — und `/kalender?von=…&raster=stunde`
     ist die einzige Form, in der er ankommt. Nebenbei trägt der Rückweg
     des Browsers dann, was er tragen soll. */
  validateSearch: kalenderstand,
});
/* ══════════════ Vermieten ══════════════

   **`/vermieten` ist seit dem 17.09.2026 das Vermietmodul**
   (docs/38-vermietmodul.md): eine Fläche, zwei Fragen, eine Sammlung. Wer
   „Jetzt vermieten" klickt, landet dort wie bisher — nur kann er jetzt
   mehr als eine Zeile.

   **Die alte Schnellerfassung steht daneben** und nicht an ihrer Stelle.
   Der Tagesstreifen (17.09.), die weiteren Zeilenarten und der
   Gespannvorschlag (beide 18.09.) sind inzwischen in der Fläche — was sie
   hält, ist seit dem 18.09.2026 vor allem **eines**: *„Das letzte Mal war
   ja gut."* Der Verlauf des Kunden, aus dem ein Griff einen ganzen Entwurf
   macht (`useVerlauf`, `useWiederholung`, `vorlageAnwenden`), samt dem
   Preisvergleich „damals gegen heute". Das ist der häufigste Anruf im
   Innendienst und schloss eine gemeldete Lücke
   (`docs/uebergabe/2026-09-16-epos-luecken.md`, Lücke 3); ihn abzuschalten
   hieße, sie wieder aufzumachen. Dazu fünf kleinere Punkte — sie stehen
   vollständig und mit Aufwand in `docs/38-vermietmodul.md` unter „Was bis
   zum Umschalten noch fehlt".

   Solange einer davon offen ist, wäre der Regelweg ohne sie ärmer als
   vorher — genau die Bedingung, die Schritt 3 des Entwurfs stellt: *„Erst
   dann, nicht vorher."*

   **Und wer sie abräumt, löscht nicht einfach zwei Dateien.**
   `stile/schnell.css` wird global geladen und trägt Klassen, die andere
   Bausteine brauchen (`.zr-*`, `.se-tag*`, `.treffer*`, `.kunde-zeile`).
   Sie muss auseinandergenommen werden, nicht entfernt. */
const vermieten = createRoute({ getParentRoute: () => wurzel, path: "/vermieten", component: Vermieten });
const vermietenKlassisch = createRoute({
  getParentRoute: () => wurzel,
  path: "/vermieten/klassisch",
  component: Schnellerfassung,
});

const angebote = createRoute({ getParentRoute: () => wurzel, path: "/angebote", component: Angebote });
/* ── Die Vorbelegung aus der Adresse (16.09.2026, seit dem 17.09.2026 hier) ──

   **Das Angebot nimmt seine Vorbelegung aus der Adresse.** Wer in der
   Auskunft gerade „Radlader, nächste Woche, 4 Tage" gefragt hat, soll von
   dort einen Griff weit zum Angebot haben — und nicht dieselben drei Dinge
   noch einmal eintippen. Alle Werte sind freiwillig; `/angebote/neu` ohne
   alles ist der Regelfall und geht ebenso auf.

   Geprüft wird jeder einzeln: Was keine UUID und kein Tag ist, kommt nicht
   in eine Preisanfrage. Eine halbe Vorbelegung ist besser als eine falsche.

   Sie stand bis zum 17.09.2026 an `/handy/angebot` — die Adresse leitet jetzt
   hierher um und nimmt alles mit (`schale/altadressen.ts`). */
const UUID_WEG = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
type Angebotsvorbelegung = { typ?: string; von?: string; bis?: string; variante?: string; kunde?: string };
function angebotsvorbelegung(such: Record<string, unknown>): Angebotsvorbelegung {
  return {
    ...(typeof such["typ"] === "string" && UUID_WEG.test(such["typ"])
      ? { typ: such["typ"] } : {}),
    ...(typeof such["von"] === "string" && /^\d{4}-\d{2}-\d{2}$/.test(such["von"])
      ? { von: such["von"] } : {}),
    ...(typeof such["bis"] === "string" && /^\d{4}-\d{2}-\d{2}$/.test(such["bis"])
      ? { bis: such["bis"] } : {}),
    ...(typeof such["variante"] === "string" && UUID_WEG.test(such["variante"])
      ? { variante: such["variante"] } : {}),
    // Die Kundennummer, nicht die UUID: Sie steht in jeder Liste und wird
    // gesprochen (`10008`). Der Router liest `?kunde=10008` als Zahl — sie
    // wird hier wieder der Text, der sie ist.
    ...((typeof such["kunde"] === "string" && such["kunde"]) || typeof such["kunde"] === "number"
      ? { kunde: String(such["kunde"]) } : {}),
  };
}
/* ── Ein Rahmen für beide Adressen (03.10.2026) ──

   Luca: *„Wenn ich ins Angebot rein gehe und die erste Position hinzufüge,
   dann kommt ein kompletter Reload."* Der Entwurf entsteht mit der ersten
   Position, und danach steht die Adresse auf `/angebote/AG-…`. Bis heute
   hingen `/angebote/neu` und `/angebote/$nr` als **zwei Wege mit je einer
   Komponente** am Baum — und ein Wechsel des Weges ist für React ein anderer
   Baum: Die Maske wurde abgebaut und neu aufgebaut, mit Lade-Skelett,
   Scrollstand oben und offenen Eingaben weg.

   Jetzt zeichnet ein **pfadloser Rahmen** den Schirm, und die beiden Wege
   darunter sind nur Adressen (`component: () => null`). Der Rahmen bleibt
   beim Wechsel von `neu` auf die Nummer derselbe Treffer, und React behält
   die Instanz — wie schon beim Sprung zwischen zwei Nummern. Die
   Vorbelegung aus der Adresse hängt am Rahmen; unter einer Nummer ist sie
   leer und schadet nicht. */
const angebotRahmen = createRoute({
  getParentRoute: () => wurzel,
  id: "angebot-rahmen",
  component: AngebotRahmen,
  validateSearch: angebotsvorbelegung,
});
const nurAdresse = () => null;
const angebotNeu = createRoute({
  getParentRoute: () => angebotRahmen,
  path: "/angebote/neu",
  component: nurAdresse,
});
/* Derselbe Schirm mit einer Nummer davor: „Angebot bauen" ist kein
   Anlegeformular, das nach dem Absenden zu ist, sondern die Bearbeitung
   eines Entwurfs. Ohne diesen Weg hätte ein Angebot, das einmal steht,
   keine bearbeitbare Seite mehr — und genau das war bis zum 15.09.2026
   der Fall. */
const angebot = createRoute({ getParentRoute: () => angebotRahmen, path: "/angebote/$nr", component: nurAdresse });

/* **Die Liste öffnet in dem Filter, aus dem heraus man sie ruft** (22.09.2026).
   Vorgabe bleibt „Zu prüfen" — dort wartet Arbeit auf einen Menschen. Die
   Kachel „draußen" auf der Startseite meint aber etwas anderes, und Luca
   stand deshalb vor einer Liste, die nicht zeigte, wonach er geklickt hatte:
   *„Ein Auftrag ist beim Kunden, der springt immer auf ‚zu prüfen'. Da muss
   er natürlich auch auf ‚offen' gehen, sodass man sieht, was draußen ist."* */
const auftraege = createRoute({
  getParentRoute: () => wurzel,
  path: "/auftraege",
  component: Auftraege,
  validateSearch: (such: Record<string, unknown>): { filter?: "offen" | "zutun" | "bereit" | "alle" } =>
    such["filter"] === "offen" || such["filter"] === "zutun"
      || such["filter"] === "bereit" || such["filter"] === "alle"
      ? { filter: such["filter"] }
      : {},
});
/* Die Ortsaufnahmen — der Pool des Aussendiensts (19-ortsaufnahme.md). */
const aufnahmen = createRoute({
  getParentRoute: () => wurzel,
  path: "/aufnahmen",
  component: Aufnahmen,
  /* `?neu=1` öffnet das Blatt „Aufmaß erfassen" (21.09.2026, Migration
     0155). Der Griff steht im Anlegeblatt hinter dem Kreis und führt von
     jedem Schirm hierher; die Adresse ist der Weg dorthin, weil das Blatt
     den Zusammenhang dieser Maske braucht (Kunden, Leistungsarten, Orte).
     Dieselbe Machart wie `?typ=` beim neuen Angebot: eine Vorbelegung,
     die die Maske liest, kein zweiter Schirm. */
  validateSearch: (such: Record<string, unknown>): { neu?: true } =>
    (such["neu"] === "1" || such["neu"] === 1 || such["neu"] === true ? { neu: true } : {}),
});
const auftrag = createRoute({
  getParentRoute: () => wurzel,
  path: "/auftraege/$nr",
  component: Vorgang,
  /* `?protokoll=<id>` schlägt ein Protokoll gleich auf (24.09.2026, Prüfung
     F-02). Die Zeile „Unterschrift" unter „Zu prüfen" führte in die Akte, und
     dort musste man das Protokoll erst suchen — Lisa: *„Beim Reingehen in den
     Punkt ist nicht auf Anhieb klar, was konkret zu tun ist."* Dieselbe
     Machart wie `?neu=1` bei den Ortsaufnahmen: eine Vorbelegung, kein
     zweiter Schirm. */
  /* `?fotos=<belegung>` öffnet den Blick auf fehlende Pflichtfotos (0173) —
     dieselbe Machart für die Zeile „Fotos" unter „Zu prüfen". */
  validateSearch: (such: Record<string, unknown>): { protokoll?: string; fotos?: string } => ({
    ...(typeof such["protokoll"] === "string" && such["protokoll"] ? { protokoll: such["protokoll"] } : {}),
    ...(typeof such["fotos"] === "string" && such["fotos"] ? { fotos: such["fotos"] } : {}),
  }),
});

const kunden = createRoute({ getParentRoute: () => wurzel, path: "/kunden", component: Kunden });
/* ══════════════ Die Suche (19.09.2026) ══════════════

   **Am Telefon ein Ort, am Schreibtisch ein Fenster.** Luca: *„Dann machst
   du nämlich unten nicht mehr ‚Kunden', sondern ‚Suche'."* Am Telefon ist
   sie deshalb eine Wurzel mit eigenem Fach (`masken/SucheTelefon.tsx`). Am
   Schreibtisch gibt es dafür das Suchfenster über jeder Maske
   (`schale/Suche.tsx`, `Strg`+`K`); die Adresse öffnet es dort und führt
   zurück, wo man war — derselbe Weg wie beim Nachschlagen. */
const suche = createRoute({
  getParentRoute: () => wurzel,
  path: "/suche",
  beforeLoad: ({ preload }) => {
    if (istTelefon()) return;
    if (!preload) sucheOeffnen();
    throw redirect({ href: zurueckVomBlatt(), replace: true });
  },
  component: SucheSeite,
});
const kundeNeu = createRoute({
  getParentRoute: () => wurzel,
  path: "/kunden/neu",
  component: KundeNeu,
  /* Ein Interessent hat weniger Pflichtfelder als ein Kunde. Welche Form
     gemeint ist, steht im Weg — sonst führt der Knopf auf der Startseite
     ins Kundenformular und der Anwender schaltet von Hand um.

     `vermieter` seit dem 16.09.2026: dieselbe Maske, dritte Rolle. Es ist
     derselbe Datensatz (Migration 0079) — ein eigenes Vermieterformular wäre
     das zweite für dieselbe Sache. */
  validateSearch: (
    such: Record<string, unknown>,
  ): { art: "kunde" | "interessent" | "vermieter" } => ({
    art: such["art"] === "interessent" ? "interessent"
      : such["art"] === "vermieter" ? "vermieter"
        : "kunde",
  }),
});
/* `luecken` (W2, 24.09.2026): Nach einer Annahme und aus „Zu prüfen" heraus
   schlägt die Akte gleich das erste Feld auf, das für die Rechnung fehlt. */
const kunde = createRoute({
  getParentRoute: () => wurzel,
  path: "/kunden/$nr",
  component: Kunde,
  validateSearch: (such: Record<string, unknown>): { luecken?: true } => (
    such["luecken"] === true || such["luecken"] === "true" || such["luecken"] === 1 || such["luecken"] === "1"
      ? { luecken: true as const } : {}
  ),
});

const ressourcen = createRoute({ getParentRoute: () => wurzel, path: "/ressourcen", component: Ressourcen });
const preise = createRoute({ getParentRoute: () => wurzel, path: "/preise", component: Preise });
/* **Der Sprung aus dem Preisstamm trägt mit, was dort fehlte.** Seit dem
   16.09.2026 legt der Preisstamm keinen Maschinentyp mehr an — er bepreist,
   was es gibt (Luca: *„Dass man da noch Maschinen anlegen kann, das soll man
   da gar nicht können."*). Wer beim Bepreisen merkt, dass ein Typ fehlt, darf
   deshalb nicht im Leeren stehen: Der Verweis führt auf das richtige Blatt,
   öffnet das Formular und bringt die Gerätegruppe mit, in der er gerade
   stand. Ein Satz zum Merken wäre die halbe Antwort. */
const weiteres = createRoute({
  getParentRoute: () => wurzel,
  path: "/weiteres",
  component: Weiteres,
  validateSearch: (
    such: Record<string, unknown>,
  ): { blatt?: string; gruppe?: string; neu?: true } => ({
    ...(typeof such["blatt"] === "string" && such["blatt"] ? { blatt: such["blatt"] } : {}),
    ...(typeof such["gruppe"] === "string" && such["gruppe"] ? { gruppe: such["gruppe"] } : {}),
    // `neu` ist ein Schalter, kein Wert: Er steht da oder nicht.
    ...(such["neu"] === true || such["neu"] === "true" ? { neu: true as const } : {}),
  }),
});

/* ══════════════ Die Arbeit draußen — seit dem 19.09.2026 ohne `/handy` ══════════════

   Luca: *„Überleg dir mal, ob diese /handy-URL … macht ja gar keinen Sinn.
   Das brauch ich ja sonst auch nicht bei normalen Websites."*

   **Die Wege heißen nach der Aufgabe, nicht nach dem Gerät** — dieselbe
   Regel wie für alle anderen oben. Welche Fassung gezeichnet wird, sagt
   `schale/geraet.ts`; die Adresse sagt nur, **was** man sehen will. Bis zum
   19.09.2026 lag das hier unter `/handy/…`, und die Adresszeile las sich
   am Telefon wie „das ist die Handyseite" — das Gegenteil dessen, was die
   Anwendung tut. Die alten Adressen gehen weiter auf
   (`schale/altadressen.ts`), dauerhaft.

   | Weg | Aufgabe |
   |---|---|
   | `/heute` | die Tagesmappe: Vorbereiten, Raus, Rein, meine Zeit |
   | `/einsatz/$belegung` | der Einsatz — und darunter seine Schritte |
   | `/aufnahmen/$id` | die Begehung einer Ortsaufnahme vor Ort |
   | `/nachfassen` | die Angebote, deren Frist läuft |

   **Alle gehen auch am Schreibtisch auf**, wie bisher: Wer nachsehen will,
   was der Fahrer sieht, soll es aufrufen können. Die Maske wächst dabei
   nicht in die Breite (`stile/handy.css`, ganz unten). Eigene Wege haben sie
   nur, weil es die Arbeit am Schreibtisch nicht gibt
   (40-mobiles-grundgeruest.md, „Die Regel für Masken"). */
const heute = createRoute({ getParentRoute: () => wurzel, path: "/heute", component: Heute });

/* ══════════════ Der Einsatz (17.09.2026) ══════════════

   Luca: *„Meine Aufträge heute → Auftrag übernehmen. Start trackt die
   Anfahrt zur Baustelle; ‚Bin auf der Baustelle' — dann läuft die
   Baustellenzeit."*

   **Ein Schirm für den ganzen Einsatz** (`masken/handy/Einsatz.tsx`), und
   darunter seine Schritte. Die Arbeitsgrundlage nennt ihn „eine echte
   Außendienst-Aufgabe" — deshalb ein eigener Weg und kein Blatt über der
   Tagesliste: Ein Rückweg des Browsers soll denselben Einsatz wiederfinden,
   und wer das Telefon weglegt, soll beim Aufwachen dort stehen, wo er war.

   **Die Vorbereitung ist seit dem 19.09.2026 ein Schritt darunter**
   (`/einsatz/$belegung/vorbereiten`, vorher `/handy/vorbereiten/$belegung`).
   Sie ist der erste Schritt desselben Einsatzes — der Einsatzschirm führt
   hinein und sie wieder heraus —, und eine Adresse, die das zeigt, ist die
   ehrlichere. */
const einsatz = createRoute({
  getParentRoute: () => wurzel, path: "/einsatz/$belegung", component: HandyEinsatz,
});
const einsatzVorbereiten = createRoute({
  getParentRoute: () => wurzel, path: "/einsatz/$belegung/vorbereiten", component: HandyVorbereitung,
});
const einsatzUebergabe = createRoute({
  getParentRoute: () => wurzel, path: "/einsatz/$belegung/uebergabe", component: HandyUebergabe,
});
const einsatzRuecknahme = createRoute({
  getParentRoute: () => wurzel, path: "/einsatz/$belegung/ruecknahme", component: HandyRuecknahme,
});
const einsatzZusammenfassung = createRoute({
  getParentRoute: () => wurzel, path: "/einsatz/$belegung/zusammenfassung", component: HandyZusammenfassung,
});
const einsatzTagesabschluss = createRoute({
  getParentRoute: () => wurzel, path: "/einsatz/$belegung/tagesabschluss", component: HandyTagesabschluss,
});

/* ══════════════ Drei Blätter mit Adresse ══════════════

   „Mehr", das Nachschlagen und der Preis sind **Blätter über der Maske**,
   keine Orte (`schale/Mehrblatt.tsx`, `masken/NachschlagenTelefon.tsx`). Die
   Leiste unten öffnet sie mit einem Tipper, ohne Router. Eine Adresse haben
   sie trotzdem: „Mehr" verlinkt das Nachschlagen als Zeile, ein Lesezeichen
   oder ein geteilter Link soll aufgehen, und `?typ=…&von=…&tage=…` bringt
   den Preis vorbelegt mit. Jede öffnet ihr Blatt und führt dorthin zurück,
   wo man gerade war — das Blatt lässt die Maske darunter stehen. Wer die
   Adresse frisch aufruft, landet auf `/heute`.

   **Seit Signal (Paket 5, 07.10.2026) gilt das am Telefon nur noch für das
   Nachschlagen.** „Mehr" und „Preis" sind dort Seiten (Muster
   `navigation.md` § 2, Lucas Entscheidung F5): `MehrSeite` und `PreisTelefon`.

   Sie hießen bis zum 19.09.2026 `/handy/mehr`, `/handy/nachschlagen` und
   `/handy/preis`; die alten gehen weiter auf (`schale/altadressen.ts`).

   **Nicht beim Vorladen.** `defaultPreload: "intent"` fragt die Route schon,
   wenn der Daumen auf dem Verweis liegt; aufgehen darf das Blatt erst beim
   Aufruf. */
let letzterWeg: string | null = null;
/** Der Pfad der Maske, die gerade steht — für die neue Fassung beim Wegwechsel (oben). */
let letzterPfad: string | null = null;
const zurueckVomBlatt = (): string =>
  (letzterWeg && !IST_BLATTWEG.test(letzterWeg) ? letzterWeg : "/heute");

const nachschlagenWeg = createRoute({
  getParentRoute: () => wurzel,
  path: "/nachschlagen",
  beforeLoad: ({ preload }) => {
    if (!preload) nachschlagenOeffnen();
    throw redirect({ href: zurueckVomBlatt(), replace: true });
  },
});

const preisWeg = createRoute({
  getParentRoute: () => wurzel,
  path: "/preis",
  validateSearch: (
    such: Record<string, unknown>,
  ): { typ?: string; von?: string; tage?: number } => {
    const tage = Number(such["tage"]);
    return {
      ...(typeof such["typ"] === "string" && such["typ"] ? { typ: such["typ"] } : {}),
      // Ein Tag, nicht irgendein Text — wie bei `/planung?von=`.
      ...(typeof such["von"] === "string" && /^\d{4}-\d{2}-\d{2}$/.test(such["von"])
        ? { von: such["von"] }
        : {}),
      ...(Number.isInteger(tage) && tage >= 1 && tage <= 365 ? { tage } : {}),
    };
  },
  beforeLoad: ({ preload, search }) => {
    /* Am Telefon eine Seite (Signal, Paket 5): vorbelegt wird über die Adresse
       wie bisher, gezeichnet wird `PreisTelefon`. */
    if (istTelefon()) {
      if (!preload) nachschlagenVorbelegen(search);
      return;
    }
    if (!preload) {
      nachschlagenVorbelegen(search);
      nachschlagenOeffnen();
    }
    throw redirect({ href: zurueckVomBlatt(), replace: true });
  },
  component: PreisTelefon,
});

/* „Mehr" gibt es nur am Telefon — dort unten in der Leiste. Anderswo führt die
   Adresse auf den Einstieg und öffnet nichts. */
const mehrWeg = createRoute({
  getParentRoute: () => wurzel,
  path: "/mehr",
  beforeLoad: () => {
    /* Am Telefon die Wurzelseite „Mehr" (Signal, Paket 5); anderswo gibt es
       sie nicht, und die Adresse führt zurück. */
    if (istTelefon()) return;
    throw redirect({ href: zurueckVomBlatt(), replace: true });
  },
  component: MehrSeite,
});

/* Die Begehung einer Ortsaufnahme (19-ortsaufnahme.md) und das Nachfassen
   (0112). Sie gehören dem Außendienst **kaufmännisch** — demselben Menschen,
   der zwischen zwei Terminen im Auto sitzt (H10 im Interview).

   **Die Begehung liegt unter ihrer Aufnahme** (`/aufnahmen/$id`, bis zum
   19.09.2026 `/handy/aufnahmen/$id`): Wer in der Liste eine Aufnahme tippt,
   ist in dieser Aufnahme — und die Liste darüber ist der Rückweg. Am
   Schreibtisch gibt es keine eigene Maske für eine einzelne Aufnahme; wer
   dort `/aufnahmen/$id` öffnet, sieht dieselbe Begehung, schmal in der Mitte.

   **Das Nachfassen bleibt vorerst ein eigener Schirm**: Sein Ziel ist die
   Angebotsliste mit einem Filter „Frist läuft" (40-mobiles-grundgeruest.md),
   und die hat die Griffe der Zeile — anrufen, Wiedervorlage — noch nicht. */
const begehung = createRoute({
  getParentRoute: () => wurzel, path: "/aufnahmen/$id", component: Ortsbegehung,
});
const nachfassen = createRoute({
  getParentRoute: () => wurzel, path: "/nachfassen", component: Nachfassen,
});

/* ══════════════ Die alten Adressen unter `/handy` ══════════════

   Sie werden an der Wurzel übersetzt, als Allererstes (`beforeLoad` oben,
   `schale/altadressen.ts`). Der Weg hier trägt keine Maske; er steht nur,
   damit der Router die Adresse als seine erkennt und nicht als „nicht
   gefunden" behandelt, bevor die Wurzel sie umleiten kann. Ein Splat und
   kein Weg je alter Adresse: So ist `to="/handy…"` im Code ein Typfehler —
   ein vergessener Verweis fällt beim Bauen auf, nicht beim Tippen draußen. */
const altHandyRest = createRoute({ getParentRoute: () => wurzel, path: "/handy/$" });

const abrechnung = createRoute({ getParentRoute: () => wurzel, path: "/abrechnung", component: Abrechnung });
/* **Die Belegsuche** (18.09.2026) — alle Belege live aus Lexware Office.
   Die Frage steht in der Adresse (`?kunde=10008&art=rechnung&von=…`), damit
   sie sich weiterschicken lässt und die globale Suche im Kopf direkt auf
   einen Beleg springen kann (`?beleg=<id>`). Geprüft wird jeder Wert in
   `daten/belege.ts`, `belegadresse`. */
const belege = createRoute({
  getParentRoute: () => wurzel,
  path: "/belege",
  component: Belege,
  validateSearch: belegadresse,
});
const einstellungen = createRoute({ getParentRoute: () => wurzel, path: "/einstellungen", component: Einstellungen });
/* **Die Seite steht in der Adresse** (04.10.2026, `masken/einstellungen/seiten.ts`).
   Ein alter Name (`/einstellungen/jobs`, `/einstellungen/kalender`) führt
   auf die neue Seite, ein unbekannter auf die Übersicht — eine Adresse, die
   ins Leere läuft, ist schlimmer als eine, die woanders ankommt. */
const einstellungsseite = createRoute({
  getParentRoute: () => wurzel,
  path: "/einstellungen/$seite",
  component: Einstellungen,
  /* `?lauf=<art>` schlägt unter Hintergrundläufe diesen Lauf auf — der Weg
     einer Meldung „Hintergrundlauf gescheitert" (15.10.2026). */
  validateSearch: (such: Record<string, unknown>): { lauf?: string } =>
    (typeof such["lauf"] === "string" && such["lauf"] ? { lauf: such["lauf"] } : {}),
  beforeLoad: ({ params }) => {
    const { seite, umleiten } = seiteAusAdresse(params.seite);
    if (!umleiten) return;
    if (seite) throw redirect({ to: "/einstellungen/$seite", params: { seite }, replace: true });
    throw redirect({ to: "/einstellungen", replace: true });
  },
});

/* **Das Handbuch — für jeden, der angemeldet ist** (21.09.2026). Es nennt
   bewusst **kein** Recht (`schale/rechte.ts`, `WEGRECHTE`): Es erklärt die
   Anwendung, und wer es am meisten braucht, hat die wenigsten Rechte. Die
   Einstellungen daneben hängen an `verwalten` — das Handbuch nicht. */
const handbuch = createRoute({ getParentRoute: () => wurzel, path: "/handbuch", component: Handbuch });

/* **Mein Konto — für jeden, der angemeldet ist** (E4, 04.10.2026). Wie das
   Handbuch ohne Recht davor (`schale/rechte.ts`): Den eigenen zweiten Faktor
   richtet jeder selbst ein. */
const konto = createRoute({ getParentRoute: () => wurzel, path: "/konto", component: Konto });

/* ══════════════ Wohin die nackte Adresse führt ══════════════

   **Dorthin, wo die Rolle beginnt — und das weiß erst die Rolle.** Die
   Startadresse der installierten App ist `/` (`public/site.webmanifest`),
   und von hier aus schickt der Wegwächter an der Wurzel jeden auf seinen
   Einstieg (`schale/rechte.ts`, `einstieg`): den Fahrer auf `/heute`, wer
   verkauft oder disponiert auf `/start`.

   **Bis zum 19.09.2026 riet diese Route vorher.** Solange die Sitzung noch
   nicht feststand, schickte sie das Telefon auf `/handy` und den
   Schreibtisch auf `/start` — und sobald die Rolle da war, schickte der
   Wächter den Verkäufer von `/handy` wieder weg. Zwei Umleitungen, eine
   davon falsch, und die Maske der ersten wurde dafür schon geholt. Jetzt
   wartet `/` auf die Rolle: Solange sie unbekannt ist, zeichnet die Schale
   ohnehin keine Maske (sie prüft die Sitzung oder zeigt die Anmeldung), und
   danach gibt es genau **eine** Umleitung, und die stimmt.

   Luca, 16.09.2026, über den Menüpunkt „Handy", der bis dahin hierher
   führte: *„Das ergibt sich ja daraus, wenn ich im Handy eingeloggt bin."* */
const index = createRoute({ getParentRoute: () => wurzel, path: "/" });

export const router = createRouter({
  routeTree: wurzel.addChildren([
    index, start, kalender, planung, verteilen, verteilenTag, einteilen, vermieten, vermietenKlassisch,
    angebote, angebotRahmen.addChildren([angebotNeu, angebot]),
    auftraege, auftrag, aufnahmen, begehung, nachfassen,
    kunden, kundeNeu, kunde, suche,
    ressourcen, preise, weiteres,
    heute,
    einsatz, einsatzVorbereiten, einsatzUebergabe, einsatzRuecknahme,
    einsatzTagesabschluss, einsatzZusammenfassung,
    nachschlagenWeg, preisWeg, mehrWeg,
    altHandyRest,
    abrechnung, belege, einstellungen, einstellungsseite, handbuch, konto,
  ]),
  defaultPreload: "intent",
  /* Statt „Something went wrong!" — und ein Stück, das nicht geladen werden
     konnte, lädt die Seite einmal neu (schale/Ladefehler.tsx, 02.10.2026). */
  defaultErrorComponent: Ladefehler,
  /* Statt „Not Found" auf Englisch eine deutsche Seite mit dem Weg zur
     Startseite (A-30/A-34, 03.10.2026; schale/Unbekannt.tsx). */
  defaultNotFoundComponent: Unbekannt,
});

/* Wo man zuletzt war — für die drei Blattwege, die das Blatt über genau
   dieser Maske öffnen, und für die neue Fassung beim Wegwechsel. */
router.subscribe("onResolved", ({ toLocation }) => {
  letzterWeg = toLocation.href;
  letzterPfad = toLocation.pathname;
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
